import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, CreditCard, ShoppingCart, Receipt, Search, BarChart3,
  CheckCircle, AlertCircle, Clock, Zap, Shield, ArrowRight,
} from "lucide-react";
import { cn } from "../../lib/utils";

type Product = {
  id: string;
  label: string;
  tagline: string;
  description: string;
  icon: React.FC<React.SVGProps<SVGSVGElement> & { size?: number }>;
  accent: string;
};

const PRODUCTS: Product[] = [
  {
    id: "ap-automation",
    label: "AP Automation",
    tagline: "Autonomous invoice processing",
    description: "AI-powered invoice capture with built-in fraud detection, global e-Invoicing compliance, and Medius Copilot for approvers.",
    icon: FileText,
    accent: "#DA2028",
  },
  {
    id: "payment",
    label: "Payment Automation",
    tagline: "Streamlined supplier payments",
    description: "Simplify supplier payments with virtual cards, automated approval workflows, and built-in fraud prevention.",
    icon: CreditCard,
    accent: "#2DD4BF",
  },
  {
    id: "procurement",
    label: "Procurement",
    tagline: "Control maverick spend",
    description: "Full supplier visibility, enforce approval chains, and automate buying tasks to stop rogue spending.",
    icon: ShoppingCart,
    accent: "#10B981",
  },
  {
    id: "expense",
    label: "Expense Management",
    tagline: "Powered by Expensya",
    description: "AI-driven expense reporting that auto-categorises, itemises, and submits claims — cutting burden on everyone.",
    icon: Receipt,
    accent: "#F59E0B",
  },
  {
    id: "sourcing",
    label: "Sourcing",
    tagline: "Smarter supplier selection",
    description: "Run eAuction and eTender events to find the best suppliers at the best prices in one unified platform.",
    icon: Search,
    accent: "#F87171",
  },
  {
    id: "analytics",
    label: "Analytics",
    tagline: "Spend intelligence at a glance",
    description: "Customisable dashboards and real-time spend reporting give finance leaders the visibility they need.",
    icon: BarChart3,
    accent: "#A78BFA",
  },
];

const AUTO_PLAY_INTERVAL = 4000;
const ITEM_HEIGHT = 72;

const wrap = (min: number, max: number, v: number) => {
  const rangeSize = max - min;
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min;
};

// ── Product UI Mockups ────────────────────────────────────────────────────────

function APMockup({ accent }: { accent: string }) {
  const invoices = [
    { vendor: "ACME Corp", amount: "$12,450", status: "Auto-matched", icon: "ok" },
    { vendor: "TechParts EU", amount: "$8,200", status: "In queue", icon: "wait" },
    { vendor: "Global Supply", amount: "$45,000", status: "Review", icon: "warn" },
    { vendor: "Nordic Goods AB", amount: "$3,100", status: "Auto-matched", icon: "ok" },
  ];
  return (
    <div className="w-full h-full bg-slate-900 p-5 flex flex-col gap-3 text-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4" style={{ color: accent }} />
          <span className="text-xs font-semibold">Invoice Processing</span>
        </div>
        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide" style={{ background: `${accent}25`, color: accent }}>AI Active</span>
      </div>
      <div className="flex flex-col gap-1.5 flex-1">
        {invoices.map((inv, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-slate-800/60">
            <div>
              <div className="text-[11px] font-semibold text-white">{inv.vendor}</div>
              <div className="text-[9px] text-slate-500 mt-0.5">{inv.amount}</div>
            </div>
            <div className="flex items-center gap-1.5">
              {inv.icon === "ok" && <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />}
              {inv.icon === "warn" && <AlertCircle className="w-3.5 h-3.5 text-amber-400" />}
              {inv.icon === "wait" && <Clock className="w-3.5 h-3.5 text-slate-500" />}
              <span className="text-[9px] text-slate-500">{inv.status}</span>
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="flex justify-between text-[9px] text-slate-400 mb-1.5">
          <span>Auto-match rate</span>
          <span className="text-white font-bold">94%</span>
        </div>
        <div className="h-1.5 bg-slate-700 rounded-full">
          <div className="h-1.5 rounded-full transition-all" style={{ width: "94%", backgroundColor: accent }} />
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: `${accent}12`, border: `1px solid ${accent}30` }}>
        <Zap className="w-3 h-3" style={{ color: accent }} />
        <span className="text-[9px]" style={{ color: accent }}>1,247 invoices processed today</span>
      </div>
    </div>
  );
}

function PaymentMockup({ accent }: { accent: string }) {
  const payments = [
    { vendor: "Office Depot", amount: "$5,000", due: "Due today" },
    { vendor: "AWS Cloud", amount: "$12,400", due: "Tomorrow" },
    { vendor: "Delta Air", amount: "$3,200", due: "In 3 days" },
  ];
  return (
    <div className="w-full h-full bg-slate-900 p-5 flex flex-col gap-4 text-white">
      <div className="rounded-2xl p-4 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #304140, #1A2828)" }}>
        <div className="text-[9px] text-white/30 uppercase tracking-[0.2em] mb-2">Medius Virtual Card</div>
        <div className="text-sm font-mono tracking-[0.15em] text-white/80">•••• •••• •••• 4821</div>
        <div className="flex justify-between items-end mt-3">
          <div>
            <div className="text-[8px] text-white/25 uppercase tracking-wide">Expires</div>
            <div className="text-[10px] text-white/50 font-mono">03 / 27</div>
          </div>
          <div className="flex items-center gap-1.5">
            <Shield className="w-3 h-3" style={{ color: accent }} />
            <span className="text-[9px] font-semibold" style={{ color: accent }}>Protected</span>
          </div>
        </div>
      </div>
      <div className="flex-1">
        <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-2">Pending Payments</div>
        <div className="flex flex-col gap-1.5">
          {payments.map((p, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-slate-800/60">
              <div>
                <div className="text-[11px] font-semibold text-white">{p.vendor}</div>
                <div className="text-[9px] text-slate-500 mt-0.5">{p.due}</div>
              </div>
              <span className="text-[11px] font-bold text-white">{p.amount}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: `${accent}12`, border: `1px solid ${accent}30` }}>
        <Shield className="w-3.5 h-3.5" style={{ color: accent }} />
        <span className="text-[9px] font-semibold" style={{ color: accent }}>Fraud risk score: Low — all clear</span>
      </div>
    </div>
  );
}

function ProcurementMockup({ accent }: { accent: string }) {
  const suppliers = [
    { name: "Dell", cat: "IT", price: "$1,200", score: 92 },
    { name: "3M", cat: "Office", price: "$850", score: 88 },
    { name: "Canon", cat: "Office", price: "$2,100", score: 85 },
    { name: "Lenovo", cat: "IT", price: "$980", score: 90 },
  ];
  return (
    <div className="w-full h-full bg-slate-900 p-5 flex flex-col gap-3 text-white">
      <div className="flex items-center gap-2">
        <ShoppingCart className="w-4 h-4" style={{ color: accent }} />
        <span className="text-xs font-semibold">Supplier Catalog</span>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {["All", "IT", "Office", "Facilities"].map((cat, i) => (
          <span key={cat} className="px-2.5 py-0.5 rounded-full text-[9px] font-semibold cursor-pointer" style={i === 0 ? { background: accent, color: "white" } : { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.45)" }}>
            {cat}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 flex-1">
        {suppliers.map((s, i) => (
          <div key={i} className="p-3 rounded-xl bg-slate-800/60 flex flex-col gap-1">
            <div className="text-[9px] text-slate-500 uppercase tracking-wide">{s.cat}</div>
            <div className="text-xs font-bold text-white">{s.name}</div>
            <div className="text-xs font-bold" style={{ color: accent }}>{s.price}</div>
            <div className="text-[9px] text-slate-500">Score: {s.score}/100</div>
          </div>
        ))}
      </div>
      <div>
        <div className="flex justify-between text-[9px] text-slate-400 mb-1.5">
          <span>Q4 IT Budget</span>
          <span className="text-white font-bold">$48K / $70K</span>
        </div>
        <div className="h-1.5 bg-slate-700 rounded-full">
          <div className="h-1.5 rounded-full" style={{ width: "69%", backgroundColor: accent }} />
        </div>
      </div>
    </div>
  );
}

function ExpenseMockup({ accent }: { accent: string }) {
  const categories = [
    { name: "Travel", pct: 42, amount: "$4,200" },
    { name: "Software", pct: 21, amount: "$2,100" },
    { name: "Meals", pct: 15, amount: "$1,500" },
    { name: "Other", pct: 22, amount: "$2,200" },
  ];
  const claims = [
    { name: "J. Kowalski", amount: "$342", ok: true },
    { name: "A. Smith", amount: "$1,240", ok: false },
    { name: "M. Chen", amount: "$780", ok: true },
  ];
  return (
    <div className="w-full h-full bg-slate-900 p-5 flex flex-col gap-3 text-white">
      <div className="flex items-center gap-2">
        <Receipt className="w-4 h-4" style={{ color: accent }} />
        <span className="text-xs font-semibold">Expense Management</span>
      </div>
      <div className="flex flex-col gap-2">
        {categories.map((cat, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[9px] text-slate-400 w-14 shrink-0">{cat.name}</span>
            <div className="flex-1 h-1.5 bg-slate-700 rounded-full">
              <div className="h-1.5 rounded-full" style={{ width: `${cat.pct}%`, backgroundColor: accent }} />
            </div>
            <span className="text-[9px] text-slate-300 font-mono w-12 text-right">{cat.amount}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-800 pt-2 flex-1">
        <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-2">Recent Claims</div>
        <div className="flex flex-col gap-1.5">
          {claims.map((c, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2 rounded-xl bg-slate-800/60">
              <span className="text-[11px] text-white font-medium">{c.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-white">{c.amount}</span>
                <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold" style={c.ok ? { background: "#10B98120", color: "#10B981" } : { background: `${accent}20`, color: accent }}>
                  {c.ok ? "Approved" : "Pending"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: `${accent}12`, border: `1px solid ${accent}30` }}>
        <Zap className="w-3 h-3" style={{ color: accent }} />
        <span className="text-[9px]" style={{ color: accent }}>AI auto-categorised 98% of receipts</span>
      </div>
    </div>
  );
}

function SourcingMockup({ accent }: { accent: string }) {
  const suppliers = [
    { name: "Vendor A", price: 92, quality: 88, delivery: 95, score: 91, winner: true },
    { name: "Vendor B", price: 85, quality: 92, delivery: 78, score: 85, winner: false },
    { name: "Vendor C", price: 78, quality: 85, delivery: 90, score: 84, winner: false },
  ];
  return (
    <div className="w-full h-full bg-slate-900 p-5 flex flex-col gap-3 text-white">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4" style={{ color: accent }} />
        <span className="text-xs font-semibold">eSourcing Event</span>
      </div>
      <div className="px-3 py-2 rounded-xl bg-slate-800/40 flex items-center gap-2">
        <span className="text-[10px] text-slate-300 font-medium">IT Hardware RFQ — Q1 2026</span>
        <span className="ml-auto px-2 py-0.5 rounded-full text-[8px] font-bold uppercase" style={{ background: `${accent}25`, color: accent }}>Live</span>
      </div>
      <div className="flex-1 flex flex-col">
        <div className="grid grid-cols-4 gap-1 mb-2 px-1">
          {["Supplier", "Price", "Quality", "Score"].map((h) => (
            <span key={h} className="text-[8px] text-slate-500 font-bold uppercase">{h}</span>
          ))}
        </div>
        {suppliers.map((s, i) => (
          <div key={i} className="grid grid-cols-4 gap-1 items-center py-2 border-t border-slate-800">
            <div className="flex items-center gap-1.5">
              <span className={cn("w-2.5 h-2.5 rounded-full shrink-0", s.winner ? "bg-emerald-400" : "bg-slate-700")} />
              <span className="text-[10px] text-white">{s.name}</span>
            </div>
            <span className="text-[9px] text-slate-300">{s.price}</span>
            <span className="text-[9px] text-slate-300">{s.quality}</span>
            <span className="text-[10px] font-bold" style={{ color: s.winner ? "#10B981" : "rgba(255,255,255,0.35)" }}>{s.score}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-3 py-2.5 rounded-xl" style={{ background: "#10B98115", border: "1px solid #10B98130" }}>
        <span className="text-[10px] text-emerald-400 font-semibold">Vendor A wins — 12% savings</span>
        <ArrowRight className="w-3.5 h-3.5 text-emerald-400" />
      </div>
    </div>
  );
}

function AnalyticsMockup({ accent }: { accent: string }) {
  const bars = [40, 55, 48, 72, 65, 80, 58, 75, 88, 62, 78, 95];
  const kpis = [
    { label: "Total Spend", value: "$4.2M", change: "+8%", up: true },
    { label: "Savings", value: "$380K", change: "+12%", up: true },
    { label: "Suppliers", value: "247", change: "-5%", up: false },
    { label: "Cycle Time", value: "2.3d", change: "-18%", up: true },
  ];
  return (
    <div className="w-full h-full bg-slate-900 p-5 flex flex-col gap-3 text-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4" style={{ color: accent }} />
          <span className="text-xs font-semibold">Spend Analytics</span>
        </div>
        <span className="text-[9px] text-slate-500">Q4 2025</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {kpis.map((k, i) => (
          <div key={i} className="px-3 py-2.5 rounded-xl bg-slate-800/60">
            <div className="text-[8px] text-slate-500 mb-0.5">{k.label}</div>
            <div className="text-sm font-bold text-white">{k.value}</div>
            <div className="text-[9px] font-semibold" style={{ color: k.up ? "#10B981" : "#F87171" }}>{k.change}</div>
          </div>
        ))}
      </div>
      <div className="flex-1 rounded-xl bg-slate-800/40 p-3 flex flex-col gap-2">
        <div className="text-[8px] text-slate-500 uppercase tracking-widest">Invoice Volume — 12mo</div>
        <div className="flex-1 flex items-end gap-1">
          {bars.map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-t-sm transition-all"
              style={{ height: `${h}%`, background: i === bars.length - 1 ? accent : `${accent}45` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main carousel ─────────────────────────────────────────────────────────────

export function FeatureCarousel() {
  const [step, setStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const currentIndex = ((step % PRODUCTS.length) + PRODUCTS.length) % PRODUCTS.length;

  const nextStep = useCallback(() => setStep((p) => p + 1), []);

  const handleChipClick = (index: number) => {
    const diff = (index - currentIndex + PRODUCTS.length) % PRODUCTS.length;
    if (diff > 0) setStep((s) => s + diff);
  };

  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(nextStep, AUTO_PLAY_INTERVAL);
    return () => clearInterval(id);
  }, [nextStep, isPaused]);

  const getCardStatus = (index: number) => {
    const diff = index - currentIndex;
    const len = PRODUCTS.length;
    let d = diff;
    if (diff > len / 2) d -= len;
    if (diff < -len / 2) d += len;
    if (d === 0) return "active";
    if (d === -1) return "prev";
    if (d === 1) return "next";
    return "hidden";
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
      <div className="relative overflow-hidden rounded-[2rem] lg:rounded-[3rem] flex flex-col lg:flex-row min-h-[600px] lg:aspect-video border border-slate-200 shadow-2xl shadow-slate-200/60">

        {/* Left — scrolling product chips */}
        <div className="w-full lg:w-[42%] min-h-[320px] md:min-h-[420px] lg:h-full relative z-30 flex flex-col items-start justify-center overflow-hidden px-8 md:px-14 lg:pl-14 bg-[#1A2828]">
          <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-[#1A2828] to-transparent z-40 pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#1A2828] to-transparent z-40 pointer-events-none" />

          <div className="relative w-full h-full flex items-center justify-center lg:justify-start z-20">
            {PRODUCTS.map((product, index) => {
              const isActive = index === currentIndex;
              const distance = index - currentIndex;
              const wrappedDistance = wrap(-(PRODUCTS.length / 2), PRODUCTS.length / 2, distance);
              const Icon = product.icon;

              return (
                <motion.div
                  key={product.id}
                  style={{ height: ITEM_HEIGHT, width: "fit-content" }}
                  animate={{
                    y: wrappedDistance * ITEM_HEIGHT,
                    opacity: Math.max(0, 1 - Math.abs(wrappedDistance) * 0.28),
                  }}
                  transition={{ type: "spring", stiffness: 90, damping: 22, mass: 1 }}
                  className="absolute flex items-center justify-start"
                >
                  <button
                    onClick={() => handleChipClick(index)}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    className={cn(
                      "relative flex items-center gap-3 px-5 py-3 rounded-full transition-all duration-500 text-left border cursor-pointer",
                      isActive
                        ? "text-white border-transparent shadow-lg z-10"
                        : "bg-transparent text-white/45 border-white/12 hover:border-white/30 hover:text-white/75"
                    )}
                    style={isActive ? { backgroundColor: product.accent, borderColor: product.accent } : {}}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="font-medium text-sm tracking-tight whitespace-nowrap">
                      {product.label}
                    </span>
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Right — 3D card stack with product mockups */}
        <div className="flex-1 min-h-[480px] md:min-h-[560px] lg:h-full relative bg-slate-50 flex items-center justify-center py-14 md:py-20 lg:py-14 px-6 md:px-10 overflow-hidden border-t lg:border-t-0 lg:border-l border-slate-200">
          <div className="relative w-full max-w-[340px] aspect-[4/5] flex items-center justify-center">
            {PRODUCTS.map((product, index) => {
              const status = getCardStatus(index);
              const isActive = status === "active";
              const isPrev = status === "prev";
              const isNext = status === "next";

              return (
                <motion.div
                  key={product.id}
                  initial={false}
                  animate={{
                    x: isActive ? 0 : isPrev ? -80 : isNext ? 80 : 0,
                    scale: isActive ? 1 : isPrev || isNext ? 0.87 : 0.7,
                    opacity: isActive ? 1 : isPrev || isNext ? 0.35 : 0,
                    rotate: isPrev ? -4 : isNext ? 4 : 0,
                    zIndex: isActive ? 20 : isPrev || isNext ? 10 : 0,
                    pointerEvents: isActive ? "auto" : "none",
                  }}
                  transition={{ type: "spring", stiffness: 260, damping: 25, mass: 0.8 }}
                  className="absolute inset-0 rounded-[1.75rem] overflow-hidden border-4 border-white shadow-2xl shadow-slate-400/30 origin-center"
                >
                  {/* Mockup content */}
                  {product.id === "ap-automation" && <APMockup accent={product.accent} />}
                  {product.id === "payment" && <PaymentMockup accent={product.accent} />}
                  {product.id === "procurement" && <ProcurementMockup accent={product.accent} />}
                  {product.id === "expense" && <ExpenseMockup accent={product.accent} />}
                  {product.id === "sourcing" && <SourcingMockup accent={product.accent} />}
                  {product.id === "analytics" && <AnalyticsMockup accent={product.accent} />}

                  {/* Active overlay with description */}
                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-x-0 bottom-0 px-5 pb-5 pt-14 bg-gradient-to-t from-slate-900/95 via-slate-900/50 to-transparent pointer-events-none"
                      >
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest mb-2" style={{ background: `${product.accent}25`, color: product.accent }}>
                          {product.tagline}
                        </span>
                        <p className="text-white text-[13px] font-medium leading-snug">
                          {product.description}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Top label dot */}
                  <div className={cn("absolute top-4 left-4 flex items-center gap-2 transition-opacity duration-300", isActive ? "opacity-100" : "opacity-0")}>
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: product.accent, boxShadow: `0 0 6px ${product.accent}` }} />
                    <span className="text-white/60 text-[9px] font-semibold uppercase tracking-widest">{product.label}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeatureCarousel;
